import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

// ─── CSV helpers ────────────────────────────────────────────────────────────
const QUOTA_CSV_HEADERS = [
  'FactoryShortCode', 'FactoryName', 'Grade', 'Quantity_MT',
  'Rate_per_Kg', 'Status', 'Validity_Date', 'Remark'
];

const QUOTA_SAMPLE_ROWS = [
  ['SGM001', 'Shree Ganesh Sugar Mills', 'SS-30', '500', '45', 'Available', '2025-12-31', ''],
  ['MRS002', 'Maharashtra Rajya Sahakari', 'S1-30', '750', '46.5', 'Limited', '2025-12-15', 'update'],
  ['VSI003', 'Vasantdada Sugar Institute', 'M-30', '300', '47', 'Available', '', ''],
];

function downloadSampleCSV() {
  const rows = [QUOTA_CSV_HEADERS, ...QUOTA_SAMPLE_ROWS];
  const csv = rows?.map(r => r?.join(','))?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'quota_listings_sample.csv';
  a?.click();
  URL.revokeObjectURL(url);
}

function downloadCurrentDataCSV(quotas) {
  const headers = QUOTA_CSV_HEADERS;
  const dataRows = quotas?.map(q => [
    q?.shortCode || '',
    q?.factory || '',
    q?.grade || '',
    q?.quantity || '',
    q?.pricePerTon || '',
    q?.availabilityStatus || '',
    q?.offerValidUntil || q?.availableFromDate || '',
    '',
  ]?.map(v => `"${String(v)?.replace(/"/g, '""')}"`).join(','));
  const csv = [headers?.join(','), ...dataRows]?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const date = new Date()?.toISOString()?.split('T')?.[0];
  a.download = `quotas_current_data_${date}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const lines = text?.trim()?.split('\n')?.filter(l => l?.trim());
  if (lines?.length < 2) return [];
  const headers = lines?.[0]?.split(',')?.map(h => h?.trim());
  return lines?.slice(1)?.map(line => {
    const vals = line?.split(',')?.map(v => v?.trim());
    const obj = {};
    headers?.forEach((h, i) => { obj[h] = vals?.[i] ?? ''; });
    return obj;
  });
}

function downloadResultsCSV(rows, resultRows) {
  const headers = [...QUOTA_CSV_HEADERS, 'Update_Status', 'Message'];
  const dataRows = rows?.map((row, i) => {
    const r = resultRows?.[i] || {};
    return [
      row?.FactoryShortCode || '',
      row?.FactoryName || '',
      row?.Grade || '',
      row?.Quantity_MT || '',
      row?.Rate_per_Kg || '',
      row?.Status || '',
      row?.Validity_Date || '',
      row?.Remark || '',
      r?.status || '',
      r?.message || '',
    ]?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(',');
  });
  const csv = [headers?.join(','), ...dataRows]?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const date = new Date()?.toISOString()?.split('T')?.[0];
  a.download = `upload_results_${date}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
}

// ─── CSV Upload Modal ────────────────────────────────────────────────────────
const QuotaCSVUploadModal = ({ onClose, onUpload }) => {
  const fileRef = useRef(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.name?.endsWith('.csv')) { setError('Please upload a .csv file.'); return; }
    setError('');
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const rows = parseCSV(ev?.target?.result);
        const processed = await onUpload(rows);
        setResult(processed);
        downloadResultsCSV(rows, processed?.resultRows);
      } catch {
        setError('Failed to parse CSV. Please check the format.');
      }
      setLoading(false);
    };
    reader?.readAsText(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
      <div className="rounded-2xl shadow-xl w-full max-w-md" style={{ backgroundColor: '#fdf6ec', border: '1px solid #e5d9cc' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#e5d9cc', backgroundColor: '#f8f1e9', borderRadius: '1rem 1rem 0 0' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="Upload" size={18} color="#d97706" />
            </div>
            <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Bulk Upload — Quota Listings</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-amber-100">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
            <p className="font-semibold mb-1" style={{ color: '#92400e' }}>Required CSV Columns:</p>
            <p style={{ color: '#78350f' }}>FactoryShortCode, FactoryName, Grade (SS-30/S1-30/S2-30/M-30), Quantity_MT, Rate_per_Kg, Status (Available/Limited), Validity_Date, Remark</p>
            <p className="mt-1.5 font-medium" style={{ color: '#92400e' }}>💡 Remark = "update" → updates existing record. Blank = skip.</p>
          </div>
          <button onClick={downloadSampleCSV} className="flex items-center gap-2 text-sm font-semibold underline" style={{ color: '#d97706' }}>
            <Icon name="Download" size={15} />Download Sample CSV
          </button>
          {!result && (
            <div onClick={() => fileRef?.current?.click()} className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer py-8 transition-colors hover:bg-amber-50" style={{ borderColor: '#f59e0b' }}>
              <Icon name="FileText" size={32} color="#f59e0b" />
              <p className="text-sm font-semibold" style={{ color: '#374151' }}>Click to select CSV file</p>
              <p className="text-xs" style={{ color: '#9ca3af' }}>.csv files only</p>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
            </div>
          )}
          {loading && <p className="text-sm text-center" style={{ color: '#6b7280' }}>Processing…</p>}
          {error && <p className="text-sm font-medium" style={{ color: '#dc2626' }}>{error}</p>}
          {result && (
            <div className="rounded-xl p-4 space-y-2" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="CheckCircle" size={18} color="#16a34a" />
                <p className="font-bold text-sm" style={{ color: '#15803d' }}>Upload Complete</p>
              </div>
              <p className="text-base font-semibold" style={{ color: '#166534' }}>
                {result?.updated} row{result?.updated !== 1 ? 's' : ''} updated successfully, {result?.skipped} row{result?.skipped !== 1 ? 's' : ''} skipped
              </p>
              {result?.invalid > 0 && <p className="text-sm" style={{ color: '#b45309' }}>⚠️ Invalid: <strong>{result?.invalid}</strong> row{result?.invalid !== 1 ? 's' : ''} (missing required fields)</p>}
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t" style={{ borderColor: '#bbf7d0' }}>
                <Icon name="Download" size={14} color="#16a34a" />
                <p className="text-xs" style={{ color: '#15803d' }}>Results CSV downloaded automatically</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border text-sm font-semibold" style={{ borderColor: '#d1c4b0', color: '#374151', backgroundColor: '#ffffff' }}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {result && (
            <button onClick={() => { setResult(null); if (fileRef?.current) fileRef.current.value = ''; }} className="flex-1 py-2.5 rounded-lg text-sm font-bold" style={{ backgroundColor: '#f59e0b', color: '#000000' }}>
              Upload Another
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const QuotaManagementTab = () => {
  const [selectedFactory, setSelectedFactory] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [quotaData, setQuotaData] = useState({
    quantity: '',
    pricePerTon: '',
    grade: '',
    availabilityStatus: 'available',
    availableFromDate: '',
    offerValidUntil: ''
  });

  const [existingQuotas, setExistingQuotas] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editModal, setEditModal] = useState({ show: false, quota: null });
  const [editFormData, setEditFormData] = useState({});
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, quotaId: null });

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [preset, setPreset] = useState('this_fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const { t } = useTranslation();

  const availableGrades = ['SS-30', 'S1-30', 'S2-30', 'M-30', 'Dim Colour', 'Damaged'];

  const availabilityStatuses = [
    { value: 'available', label: 'Available (Live Stock)', color: 'green' },
    { value: 'pending_approval', label: 'Pending for Approval', color: 'yellow' },
    { value: 'not_available', label: 'Not Available', color: 'red' },
    { value: 'available_from', label: 'Available from specific date', color: 'blue' },
    { value: 'offer_valid', label: 'Offer valid up to specific date', color: 'orange' }
  ];

  // ── Load data from Supabase ──────────────────────────────────────────────
  useEffect(() => {
    loadQuotas();
    loadFactories();

    const channel = supabase?.channel('quotas_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'quotas' }, () => {
        loadQuotas();
      })
      ?.on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'buyer_bookings', filter: 'status=eq.approved' }, () => {
        loadQuotas();
      })
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'factories' }, () => {
        loadFactories();
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, []);

  const loadQuotas = async () => {
    setLoading(true);
    const { data, error } = await supabase?.from('quotas')?.select('*')?.order('created_at', { ascending: true });
    if (!error && data) {
      setExistingQuotas(data?.map(row => ({
        id: row?.id,
        offerId: row?.offer_id || '',
        factory: row?.factory_name,
        shortCode: row?.factory_short_code || '',
        location: row?.factory_location || '',
        quantity: Number(row?.quantity),
        pricePerTon: Number(row?.price_per_ton),
        grade: row?.grade,
        availabilityStatus: row?.availability_status || 'available',
        availableFromDate: row?.available_from_date || '',
        offerValidUntil: row?.offer_valid_until || '',
        addedDate: row?.added_date || '',
        deductionNotes: row?.deduction_notes || '',
        created_at: row?.created_at || null,
      })));
    }
    setLoading(false);
  };

  const loadFactories = async () => {
    const { data, error } = await supabase?.from('factories')?.select('id, name, location, short_code')?.order('name', { ascending: true });
    if (!error && data) {
      setFactories(data?.map(f => ({
        id: f?.id,
        name: f?.name,
        location: f?.location || '',
        shortCode: f?.short_code || '',
      })));
    }
  };

  const getStatusBadgeColor = (status) => {
    const statusObj = availabilityStatuses?.find(s => s?.value === status);
    return statusObj?.color || 'gray';
  };

  const getStatusLabel = (status) => {
    const statusObj = availabilityStatuses?.find(s => s?.value === status);
    return statusObj?.label || status;
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 4000);
  };

  // ── Helper: generate Offer ID ────────────────────────────────────────────
  const generateOfferId = (shortCode, grade) => {
    const cleanCode = (shortCode || '')?.trim();
    const cleanGrade = (grade || '')?.trim()?.replace(/[\s-]/g, '');
    return `SH-${cleanCode || 'UNKNOWN'}-${cleanGrade}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d?.getTime())) return dateStr;
    return d?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  // ── CSV Upload handler ───────────────────────────────────────────────────
  const handleCSVUpload = async (rows) => {
    let updated = 0;
    let skipped = 0;
    let invalid = 0;
    const resultRows = [];

    for (const row of rows) {
      const remark = (row?.Remark || '')?.trim()?.toLowerCase();
      const factoryName = (row?.FactoryName || '')?.trim();
      const grade = (row?.Grade || '')?.trim();
      const quantityMT = parseFloat(row?.Quantity_MT);
      const pricePerKg = parseFloat(row?.Rate_per_Kg);

      if (!factoryName || !grade || isNaN(quantityMT) || isNaN(pricePerKg)) {
        invalid++;
        resultRows?.push({ status: 'Error', message: 'Missing required fields (FactoryName, Grade, Quantity_MT, Rate_per_Kg)' });
        continue;
      }

      if (!remark?.includes('update')) {
        skipped++;
        resultRows?.push({ status: 'Skipped', message: 'No changes — Remark is blank or not "update"' });
        continue;
      }

      const csvStatus = (row?.Status || '')?.trim()?.toLowerCase() === 'limited' ? 'not_available' : 'available';
      const validityDate = (row?.Validity_Date || '')?.trim();
      const shortCode = (row?.FactoryShortCode || '')?.trim();
      const offerId = generateOfferId(shortCode, grade);

      const { data: existing } = await supabase
        ?.from('quotas')
        ?.select('id')
        ?.eq('factory_short_code', shortCode)
        ?.eq('grade', grade)
        ?.maybeSingle();

      if (existing) {
        const { error: updateErr } = await supabase?.from('quotas')?.update({
          factory_name: factoryName,
          quantity: quantityMT,
          price_per_ton: pricePerKg * 1000,
          availability_status: csvStatus,
          offer_valid_until: validityDate || '',
          offer_id: offerId,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', existing?.id);
        if (updateErr) console.error('Quota update error:', updateErr);
        updated++;
        resultRows?.push({ status: 'Updated', message: `Updated Offer ID: ${offerId}` });
      } else {
        const { error: insertErr } = await supabase?.from('quotas')?.insert({
          offer_id: offerId,
          factory_name: factoryName,
          factory_short_code: shortCode,
          factory_location: '',
          grade,
          quantity: quantityMT,
          price_per_ton: pricePerKg * 1000,
          availability_status: csvStatus,
          available_from_date: '',
          offer_valid_until: validityDate || '',
          added_date: new Date()?.toISOString()?.split('T')?.[0],
        });
        if (insertErr) console.error('Quota insert error:', insertErr);
        updated++;
        resultRows?.push({ status: 'Created', message: `New Offer ID: ${offerId}` });
      }
    }

    await loadQuotas();
    showNotification('success', `${updated} rows updated successfully, ${skipped} rows skipped.`);
    return { updated, skipped, invalid, resultRows };
  };

  const handleInputChange = (field, value) => {
    setQuotaData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const selectedFactoryObj = factories?.find(f => f?.id === selectedFactory);
    const factoryName = selectedFactoryObj?.name;
    const factoryLocation = selectedFactoryObj?.location;
    const shortCode = selectedFactoryObj?.shortCode || '';
    const newGrade = quotaData?.grade;
    const offerId = generateOfferId(shortCode, newGrade);

    const now = new Date();
    const dd = String(now?.getDate())?.padStart(2, '0');
    const mm = String(now?.getMonth() + 1)?.padStart(2, '0');
    const yy = String(now?.getFullYear())?.slice(2);
    const dateStr = dd + mm + yy;

    const { count } = await supabase?.from('quotas')?.select('*', { count: 'exact', head: true })?.eq('factory_short_code', shortCode)?.gte('created_at', new Date()?.toISOString()?.split('T')?.[0]);

    const seq = String((count || 0) + 1)?.padStart(3, '0');
    const readableOfferId = (shortCode || 'UNKNOWN') + '-' + dateStr + '-' + seq;

    const { data: existing } = await supabase
      ?.from('quotas')
      ?.select('id, quantity')
      ?.eq('factory_short_code', shortCode)
      ?.eq('grade', newGrade)
      ?.maybeSingle();

    if (existing) {
      const { error: updateErr } = await supabase?.from('quotas')?.update({
        quantity: existing?.quantity + parseFloat(quotaData?.quantity),
        price_per_ton: parseFloat(quotaData?.pricePerTon),
        availability_status: quotaData?.availabilityStatus,
        available_from_date: quotaData?.availableFromDate || '',
        offer_valid_until: quotaData?.offerValidUntil || '',
        offer_id: offerId,
        readable_offer_id: readableOfferId,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', existing?.id);
      if (updateErr) console.error('Quota update error:', updateErr);
      showNotification('success', `Quota updated — Offer ID: ${offerId} (quantity added to existing record).`);
    } else {
      const { error: insertErr } = await supabase?.from('quotas')?.insert({
        offer_id: offerId,
        readable_offer_id: readableOfferId,
        factory_name: factoryName,
        factory_short_code: shortCode,
        factory_location: factoryLocation || '',
        grade: newGrade,
        quantity: parseFloat(quotaData?.quantity),
        price_per_ton: parseFloat(quotaData?.pricePerTon),
        availability_status: quotaData?.availabilityStatus,
        available_from_date: quotaData?.availableFromDate || '',
        offer_valid_until: quotaData?.offerValidUntil || '',
        added_date: new Date()?.toISOString()?.split('T')?.[0],
      });
      if (insertErr) console.error('Quota insert error:', insertErr);
      showNotification('success', `New quota created — Offer ID: ${offerId}`);
    }

    await loadQuotas();
    handleReset();
    setShowAddForm(false);
  };

  const handleReset = () => {
    setQuotaData({ quantity: '', pricePerTon: '', grade: '', availabilityStatus: 'available', availableFromDate: '', offerValidUntil: '' });
    setSelectedFactory('');
  };

  const handleDeleteQuota = (id) => {
    setDeleteConfirm({ show: true, quotaId: id });
  };

  const confirmDelete = async () => {
    await supabase?.from('quotas')?.delete()?.eq('id', deleteConfirm?.quotaId);
    await loadQuotas();
    showNotification('success', 'Quota removed successfully!');
    setDeleteConfirm({ show: false, quotaId: null });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, quotaId: null });
  };

  const handleOpenEditModal = (quota) => {
    setEditFormData({
      grade: quota?.grade || '',
      quantity: quota?.quantity || '',
      pricePerTon: quota?.pricePerTon || '',
      status: quota?.availabilityStatus === 'available' ? 'available' : 'limited',
      validityDate: quota?.offerValidUntil || quota?.availableFromDate || ''
    });
    setEditModal({ show: true, quota });
  };

  const handleCloseEditModal = () => {
    setEditModal({ show: false, quota: null });
    setEditFormData({});
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    const updatedStatus = editFormData?.status === 'available' ? 'available' : 'not_available';
    const editedQuota = editModal?.quota;
    const newGrade = editFormData?.grade;
    const shortCode = editedQuota?.shortCode || '';
    const newOfferId = generateOfferId(shortCode, newGrade);

    const { data: conflict } = await supabase
      ?.from('quotas')
      ?.select('id, quantity')
      ?.eq('factory_short_code', shortCode)
      ?.eq('grade', newGrade)
      ?.neq('id', editedQuota?.id)
      ?.maybeSingle();

    if (conflict) {
      await supabase?.from('quotas')?.update({
        quantity: conflict?.quantity + parseFloat(editFormData?.quantity),
        price_per_ton: parseFloat(editFormData?.pricePerTon),
        availability_status: updatedStatus,
        offer_valid_until: editFormData?.validityDate || '',
        available_from_date: '',
        offer_id: newOfferId,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', conflict?.id);
      await supabase?.from('quotas')?.delete()?.eq('id', editedQuota?.id);
      showNotification('success', `Grade changed — merged with existing Offer ID: ${newOfferId}`);
    } else {
      await supabase?.from('quotas')?.update({
        grade: newGrade,
        quantity: parseFloat(editFormData?.quantity),
        price_per_ton: parseFloat(editFormData?.pricePerTon),
        availability_status: updatedStatus,
        offer_valid_until: editFormData?.validityDate || '',
        available_from_date: '',
        offer_id: newOfferId,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', editedQuota?.id);
      showNotification('success', `Quota updated — Offer ID: ${newOfferId}`);
    }

    await loadQuotas();
    handleCloseEditModal();
  };

  const handlePauseQuota = async (quotaId) => {
    const quota = existingQuotas?.find(q => q?.id === quotaId);
    const isPaused = quota?.availabilityStatus === 'not_available';
    await supabase?.from('quotas')?.update({
      availability_status: isPaused ? 'available' : 'not_available',
    })?.eq('id', quotaId);
    await loadQuotas();
    showNotification('success', isPaused ? 'Quota resumed successfully.' : 'Quota paused successfully.');
  };

  // ── Filter logic ─────────────────────────────────────────────────────────
  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  const filteredQuotas = existingQuotas?.filter(quota => {
    const s = searchText?.toLowerCase() || '';
    const matchesSearch = !s ||
      quota?.factory?.toLowerCase()?.includes(s) ||
      quota?.grade?.toLowerCase()?.includes(s) ||
      quota?.shortCode?.toLowerCase()?.includes(s);

    const matchesStatus =
      statusFilter === 'all' ||
      quota?.availabilityStatus === statusFilter;

    const quotaDate = quota?.created_at ? new Date(quota.created_at) : null;
    const matchesDate =
      (!dateFrom && !dateTo) ||
      (quotaDate &&
        (!dateFrom || quotaDate >= dateFrom) &&
        (!dateTo || quotaDate <= dateTo));

    return matchesSearch && matchesStatus && matchesDate;
  });

  return (
    <div className="space-y-6">

      {/* CSV Upload Modal */}
      {showCSVModal && (
        <QuotaCSVUploadModal onClose={() => setShowCSVModal(false)} onUpload={handleCSVUpload} />
      )}

      {/* Notification Toast */}
      {notification?.show && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className={`rounded-lg p-4 border shadow-lg flex items-start gap-3 ${notification?.type === 'success' ? 'bg-green-50 border-green-200' : ''} ${notification?.type === 'error' ? 'bg-red-50 border-red-200' : ''} ${notification?.type === 'warning' ? 'bg-yellow-50 border-yellow-200' : ''}`}>
            <Icon name={notification?.type === 'success' ? 'CheckCircle' : notification?.type === 'error' ? 'XCircle' : 'AlertCircle'} size={20} className={`mt-0.5 flex-shrink-0 ${notification?.type === 'success' ? 'text-green-600' : ''} ${notification?.type === 'error' ? 'text-red-600' : ''} ${notification?.type === 'warning' ? 'text-yellow-600' : ''}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${notification?.type === 'success' ? 'text-green-900' : ''} ${notification?.type === 'error' ? 'text-red-900' : ''} ${notification?.type === 'warning' ? 'text-yellow-900' : ''}`}>{notification?.message}</p>
            </div>
            <button onClick={() => setNotification({ show: false, type: '', message: '' })} className={`flex-shrink-0 hover:opacity-70 transition-opacity ${notification?.type === 'success' ? 'text-green-600' : ''} ${notification?.type === 'error' ? 'text-red-600' : ''} ${notification?.type === 'warning' ? 'text-yellow-600' : ''}`}>
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm?.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-1" style={{ color: '#1f2937' }}>{t('quotaManager.deleteQuota', 'Remove Quota')}</h3>
                <p className="text-sm" style={{ color: '#4b5563' }}>{t('quotaManager.confirmDelete', 'Are you sure you want to delete this quota?')}</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={cancelDelete} className="px-4 py-2 text-sm font-medium rounded-lg border-2 border-gray-400 text-gray-700 bg-white hover:bg-gray-50 transition-colors">{t('common.cancel', 'Cancel')}</button>
              <button onClick={confirmDelete} className="px-4 py-2 text-sm font-bold rounded-lg bg-red-600 text-white border-2 border-red-700 hover:bg-red-700 transition-colors">{t('common.remove', 'Remove')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quota Modal */}
      {editModal?.show && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="rounded-t-2xl sm:rounded-xl border border-gray-200 shadow-2xl w-full max-w-lg max-h-screen overflow-y-auto" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ backgroundColor: '#f3f4f6', borderRadius: '0.75rem 0.75rem 0 0' }}>
              <div className="flex items-center gap-2">
                <Icon name="Edit" size={20} className="text-amber-600" />
                <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>{t('quotaManager.editQuota', 'Edit Quota')}</h2>
              </div>
              <button onClick={handleCloseEditModal} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors" style={{ color: '#6b7280' }}>
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1f2937' }}>{t('quotaManager.factoryName', 'Factory')}</label>
                <div className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium" style={{ backgroundColor: '#e9ecef', color: '#6b7280' }}>
                  {editModal?.quota?.factory}<span className="ml-2 text-xs" style={{ color: '#9ca3af' }}>({editModal?.quota?.location})</span>
                </div>
                <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>Factory cannot be changed</p>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1f2937' }}>{t('common.grade', 'Grade')} <span className="text-red-500">*</span></label>
                <select value={editFormData?.grade} onChange={(e) => handleEditFormChange('grade', e?.target?.value)} className="w-full px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors" style={{ color: '#1f2937' }}>
                  <option value="">Select grade</option>
                  {availableGrades?.map((grade) => (<option key={grade} value={grade}>{grade}</option>))}
                </select>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1f2937' }}>{t('quotaManager.quantityMT', 'Quantity (MT)')} <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={editFormData?.quantity ?? ''}
                    onChange={(e) => handleEditFormChange('quantity', e?.target?.value)}
                    min={1}
                    max={999999}
                    placeholder="e.g., 500"
                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    style={{ color: '#1f2937', fontSize: '16px' }}
                  />
                  <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>Enter quantity in metric tons</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1f2937' }}>{t('quotaManager.pricePerTon', 'Price per Ton (₹)')} <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={editFormData?.pricePerTon ?? ''}
                    onChange={(e) => handleEditFormChange('pricePerTon', e?.target?.value)}
                    min={1}
                    max={999999}
                    placeholder="e.g., 45000"
                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    style={{ color: '#1f2937', fontSize: '16px' }}
                  />
                  <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>Enter price in ₹ per metric ton</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1f2937' }}>{t('common.status', 'Status')} <span className="text-red-500">*</span></label>
                <div className="flex gap-3">
                  <button type="button" onClick={() => handleEditFormChange('status', 'available')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-semibold transition-colors ${editFormData?.status === 'available' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}>
                    <span className={`w-2 h-2 rounded-full ${editFormData?.status === 'available' ? 'bg-green-500' : 'bg-gray-400'}`}></span>{t('common.active', 'Available')}
                  </button>
                  <button type="button" onClick={() => handleEditFormChange('status', 'limited')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-semibold transition-colors ${editFormData?.status === 'limited' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}>
                    <span className={`w-2 h-2 rounded-full ${editFormData?.status === 'limited' ? 'bg-orange-500' : 'bg-gray-400'}`}></span>Limited
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#1f2937' }}>{t('quotaManager.offerValidUntil', 'Validity Date')} <span className="text-xs font-normal" style={{ color: '#9ca3af' }}>(optional)</span></label>
                <input type="date" value={editFormData?.validityDate} onChange={(e) => handleEditFormChange('validityDate', e?.target?.value)} min={new Date()?.toISOString()?.split('T')?.[0]} className="w-full px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors" style={{ color: '#1f2937' }} />
                <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>Leave blank if no expiry date applies</p>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-200" style={{ borderRadius: '0 0 0.75rem 0.75rem' }}>
              <button onClick={handleCloseEditModal} className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg border-2 border-gray-400 bg-white hover:bg-gray-50 transition-colors" style={{ color: '#374151' }}>{t('common.cancel', 'Cancel')}</button>
              <button onClick={handleSaveEdit} disabled={!editFormData?.grade || !editFormData?.quantity || !editFormData?.pricePerTon} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold rounded-lg border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: '#f59e0b', color: '#000000', borderColor: '#d97706' }}>
                <Icon name="Check" size={16} />{t('quotaManager.updateQuota', 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-green-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-green-900 mb-1">Quota Management Guidelines</h3>
            <p className="text-sm text-green-700">Add available quantity, set competitive pricing per ton (INR), specify grade details, and update availability status. All changes reflect immediately in the public marketplace listings.</p>
          </div>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex flex-wrap items-center gap-3 justify-end">
        <button onClick={downloadSampleCSV} className="flex items-center gap-1.5 text-sm font-semibold underline" style={{ color: '#d97706' }}>
          <Icon name="Download" size={15} />Download Sample CSV
        </button>
        <button onClick={() => downloadCurrentDataCSV(existingQuotas)} className="flex items-center gap-1.5 text-sm font-semibold underline" style={{ color: '#16a34a' }}>
          <Icon name="FileDown" size={15} />Download Current Data CSV
        </button>
        <button onClick={() => setShowCSVModal(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-sm border-2 transition-colors hover:bg-amber-500" style={{ backgroundColor: '#f59e0b', color: '#000000', borderColor: '#d97706' }}>
          <Icon name="Upload" size={16} />{t('quotaManager.bulkUpload', 'Bulk Upload CSV')}
        </button>
        <button onClick={() => { setShowAddForm(!showAddForm); if (!showAddForm) loadFactories(); }} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm border-2 transition-colors hover:bg-gray-100" style={{ backgroundColor: '#ffffff', color: '#374151', borderColor: '#d1d5db' }}>
          <Icon name="Plus" size={18} />{t('quotaManager.addNewQuota', 'Add New Quota')}
        </button>
      </div>

      {/* Search + Status Filter + Date Filter */}
      {!loading && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Icon name="Search" size={16} color="#9ca3af" />
              </span>
              <input
                type="text"
                placeholder="Search by factory, grade, or short code..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
                style={{ border: '1px solid #d1d5db', color: '#1e293b', backgroundColor: '#ffffff' }}
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="py-2.5 px-3 rounded-xl text-sm outline-none"
              style={{ border: '1px solid #d1d5db', color: '#374151', backgroundColor: '#ffffff', minWidth: '180px' }}
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="not_available">Not Available</option>
              <option value="available_from">Available From Date</option>
              <option value="offer_valid">Offer Valid Until</option>
            </select>
          </div>
          <DateRangeFilter
            preset={preset}
            onPresetChange={setPreset}
            customFrom={customFrom}
            onCustomFromChange={setCustomFrom}
            customTo={customTo}
            onCustomToChange={setCustomTo}
          />
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-3"></div>
          <p className="text-sm" style={{ color: '#6b7280' }}>Loading quotas…</p>
        </div>
      )}

      {/* Existing Quotas Table */}
      {!loading && filteredQuotas?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: '#f3f4f6' }}>
            <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#1f2937' }}>
              <Icon name="List" size={20} className="text-amber-600" />
              {t('quotaManager.existingQuotas', 'Existing Quotas')}
              <span className="text-sm font-normal ml-1" style={{ color: '#6b7280' }}>({filteredQuotas?.length})</span>
            </h3>
          </div>

          {/* Mobile Card Layout */}
          <div className="block md:hidden divide-y divide-gray-100">
            {filteredQuotas?.map((quota) => (
              <div key={quota?.id} className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-base" style={{ color: '#1f2937' }}>{quota?.factory}</div>
                      <div className="text-sm mt-0.5 flex items-center gap-1" style={{ color: '#6b7280' }}><Icon name="MapPin" size={13} />{quota?.location}</div>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-bold bg-amber-50 text-amber-800 border border-amber-200 flex-shrink-0">{quota?.offerId || generateOfferId(quota?.shortCode, quota?.grade)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>{t('common.grade', 'Grade')}</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">{quota?.grade}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#9ca3af' }}>{t('common.quantity', 'Quantity')}</div>
                      <div className="text-base font-bold" style={{ color: '#1f2937' }}>{quota?.quantity} <span className="text-xs font-medium" style={{ color: '#6b7280' }}>MT</span></div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#9ca3af' }}>{t('quotaManager.pricePerTon', 'Price/Ton')}</div>
                      <div className="text-base font-bold" style={{ color: '#1f2937' }}>₹{quota?.pricePerTon?.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>{t('common.status', 'Status')}</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getStatusBadgeColor(quota?.availabilityStatus)}-100 text-${getStatusBadgeColor(quota?.availabilityStatus)}-800`}>{getStatusLabel(quota?.availabilityStatus)}</span>
                  </div>
                  {quota?.deductionNotes && (
                    <div className="rounded-lg px-3 py-2 text-xs" style={{ background: '#fef9c3', border: '1px solid #fde68a' }}>
                      <p className="font-semibold mb-0.5" style={{ color: '#92400e' }}>📋 Deduction Audit</p>
                      {quota?.deductionNotes?.split('\n')?.map((note, i) => (
                        <p key={i} style={{ color: '#78350f' }}>{note}</p>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => handleOpenEditModal(quota)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border-2 border-blue-600 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"><Icon name="Edit" size={13} />{t('common.edit', 'Edit')}</button>
                    <button onClick={() => handlePauseQuota(quota?.id)} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border-2 transition-colors ${quota?.availabilityStatus === 'not_available' ? 'border-green-500 text-green-700 bg-green-50 hover:bg-green-100' : 'border-orange-500 text-orange-700 bg-orange-50 hover:bg-orange-100'}`}><Icon name={quota?.availabilityStatus === 'not_available' ? 'PlayCircle' : 'PauseCircle'} size={13} />{quota?.availabilityStatus === 'not_available' ? t('quotaManager.resumeQuota', 'Resume') : t('common.pause', 'Pause')}</button>
                    <button onClick={() => handleDeleteQuota(quota?.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border-2 border-red-500 text-red-700 bg-red-50 hover:bg-red-100 transition-colors"><Icon name="Trash2" size={13} />{t('common.remove', 'Remove')}</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>Offer ID</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>{t('quotaManager.factoryName', 'Factory')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>{t('quotaManager.quantityMT', 'Quantity (Tons)')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>{t('quotaManager.pricePerTon', 'Price/Ton (₹)')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>{t('common.grade', 'Grade')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>{t('common.status', 'Status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredQuotas?.map((quota) => (
                  <tr key={quota?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center px-2 h-2 rounded text-xs font-mono font-bold bg-amber-50 text-amber-800 border border-amber-200 flex-shrink-0">{quota?.offerId || generateOfferId(quota?.shortCode, quota?.grade)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold" style={{ color: '#1f2937' }}>{quota?.factory}</div>
                      <div className="text-sm" style={{ color: '#6b7280' }}>{quota?.location}</div>
                    </td>
                    <td className="px-6 py-4 font-medium" style={{ color: '#1f2937' }}>{quota?.quantity}</td>
                    <td className="px-6 py-4 font-medium" style={{ color: '#1f2937' }}>₹{quota?.pricePerTon?.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">{quota?.grade}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getStatusBadgeColor(quota?.availabilityStatus)}-100 text-${getStatusBadgeColor(quota?.availabilityStatus)}-800`}>{getStatusLabel(quota?.availabilityStatus)}</span>
                        {quota?.availableFromDate && (<div className="text-xs mt-1" style={{ color: '#6b7280' }}>From: {formatDate(quota?.availableFromDate)}</div>)}
                        {quota?.offerValidUntil && (<div className="text-xs mt-1" style={{ color: '#6b7280' }}>Until: {formatDate(quota?.offerValidUntil)}</div>)}
                        {quota?.deductionNotes && (
                          <div className="mt-1.5 rounded px-2 py-1 text-xs" style={{ background: '#fef9c3', border: '1px solid #fde68a', maxWidth: '220px' }}>
                            <p className="font-semibold mb-0.5" style={{ color: '#92400e' }}>📋 Deductions:</p>
                            {quota?.deductionNotes?.split('\n')?.map((note, i) => (
                              <p key={i} className="leading-snug" style={{ color: '#78350f' }}>{note}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleOpenEditModal(quota)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border-2 border-blue-600 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"><Icon name="Edit" size={13} />{t('common.edit', 'Edit')}</button>
                        <button onClick={() => handlePauseQuota(quota?.id)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border-2 transition-colors ${quota?.availabilityStatus === 'not_available' ? 'border-green-500 text-green-700 bg-green-50 hover:bg-green-100' : 'border-orange-500 text-orange-700 bg-orange-50 hover:bg-orange-100'}`}><Icon name={quota?.availabilityStatus === 'not_available' ? 'PlayCircle' : 'PauseCircle'} size={13} />{quota?.availabilityStatus === 'not_available' ? t('quotaManager.resumeQuota', 'Resume') : t('common.pause', 'Pause')}</button>
                        <button onClick={() => handleDeleteQuota(quota?.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border-2 border-red-500 text-red-700 bg-red-50 hover:bg-red-100 transition-colors"><Icon name="Trash2" size={13} />{t('common.remove', 'Remove')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No match state */}
      {!loading && existingQuotas?.length > 0 && filteredQuotas?.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center">
          <Icon name="Search" size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium" style={{ color: '#6b7280' }}>No quotas match your search or filters.</p>
          <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>Try adjusting your search, status filter, or date range.</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && existingQuotas?.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center">
          <Icon name="Package" size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium" style={{ color: '#6b7280' }}>No quotas added yet. Use "Add New Quota" or "Bulk Upload CSV" to get started.</p>
        </div>
      )}

      {/* Quota Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border-2 border-amber-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold mb-6 flex items-center gap-2" style={{ color: '#1f2937' }}>
            <Icon name="Package" size={20} className="text-amber-600" />{t('quotaManager.addNewQuota', 'Add New Quota')}
          </h3>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#1f2937' }}>Select Factory <span className="text-red-500">*</span></label>
              <select value={selectedFactory} onChange={(e) => setSelectedFactory(e?.target?.value)} required className="w-full px-4 py-2 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" style={{ color: '#1f2937' }}>
                <option value="">Choose a factory</option>
                {factories?.map((factory) => (<option key={factory?.id} value={factory?.id}>{factory?.name} - {factory?.location}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  type="number"
                  min="1"
                  max="999999"
                  step="1"
                  placeholder="e.g., 500"
                  value={quotaData?.quantity ?? ''}
                  onChange={(e) => handleInputChange('quantity', e?.target?.value)}
                  className="w-full px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  style={{ color: '#1f2937' }}
                />
              </div>
              <div>
                <input
                  type="number"
                  min="1"
                  max="999999"
                  placeholder="e.g., 45000"
                  value={quotaData?.pricePerTon ?? ''}
                  onChange={(e) => handleInputChange('pricePerTon', e?.target?.value)}
                  className="w-full px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  style={{ color: '#1f2937' }}
                />
                <p className="text-xs text-gray-500 mt-1">Above Basic Rate + Taxes as applicable</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#1f2937' }}>Sugar Grade <span className="text-red-500">*</span></label>
              <select value={quotaData?.grade} onChange={(e) => handleInputChange('grade', e?.target?.value)} required className="w-full px-4 py-2 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" style={{ color: '#1f2937' }}>
                <option value="">Select grade</option>
                {availableGrades?.map((grade) => (<option key={grade} value={grade}>{grade}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#1f2937' }}>Availability Status <span className="text-red-500">*</span></label>
              <select value={quotaData?.availabilityStatus} onChange={(e) => handleInputChange('availabilityStatus', e?.target?.value)} required className="w-full px-4 py-2 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" style={{ color: '#1f2937' }}>
                {availabilityStatuses?.map((status) => (<option key={status?.value} value={status?.value}>{status?.label}</option>))}
              </select>
            </div>
            {quotaData?.availabilityStatus === 'available_from' && (
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1f2937' }}>Available From Date <span className="text-red-500">*</span></label>
                <input type="date" value={quotaData?.availableFromDate} onChange={(e) => handleInputChange('availableFromDate', e?.target?.value)} required min={new Date()?.toISOString()?.split('T')?.[0]} className="w-full" />
              </div>
            )}
            {quotaData?.availabilityStatus === 'offer_valid' && (
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#1f2937' }}>Offer Valid Until <span className="text-red-500">*</span></label>
                <input type="date" value={quotaData?.offerValidUntil} onChange={(e) => handleInputChange('offerValidUntil', e?.target?.value)} required min={new Date()?.toISOString()?.split('T')?.[0]} className="w-full" />
              </div>
            )}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Icon name="Zap" size={18} className="text-blue-600" />
                <p className="text-sm text-blue-700"><span className="font-semibold">Real-time updates:</span> Changes will reflect immediately in marketplace listings</p>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <button type="submit" disabled={!selectedFactory} className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: '#f59e0b', color: '#000000', borderColor: '#d97706' }}>
                <Icon name="Plus" size={18} />Add New Quota
              </button>
              <button type="button" onClick={() => { handleReset(); setShowAddForm(false); }} className="px-6 py-2.5 rounded-lg font-bold text-sm border-2 border-gray-400 text-gray-700 bg-white hover:bg-gray-50 transition-colors">Cancel</button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default QuotaManagementTab;


